import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, LogIn } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useSurfaceTheme } from '../hooks/useSurfaceTheme';
import { Revelar } from '../components/landing/Revelar';
import {
  COLOR_FAMILIA,
  ICONOS_FAMILIA,
  IconoAporte,
  IconoCita,
  IconoValidar,
  IconoVoz,
} from '../components/landing/Iconos';
import {
  DiagramaSuperficies,
  DiagramaTenant,
  DiagramaTrazabilidad,
} from '../components/landing/Diagramas';
import { CATALOGO, TOTAL_HERRAMIENTAS } from './landingCatalogo';

/**
 * Landing pública de ÁGORA (/): qué es, los planes y la puerta de ingreso.
 * La raíz no manda a la app: presenta el producto; «Entrar» (/entrar)
 * conserva la redirección por rol. Diseño hero-first (retícula + orbes +
 * vidrio) sobre la paleta de tokens; el gradiente de marca se usa donde la
 * identidad lo permite: hero, palabra clave y acentos puntuales.
 * Precios de lanzamiento en USD: viven acá hasta que exista tarifario formal.
 */

const PERSONALES = [
  {
    nombre: 'Voz',
    codigo: null,
    precio: 'Gratis',
    detalle: 'para siempre',
    rasgos: [
      '2 horas de sala al mes (1 h por sesión)',
      'Hasta 5 participantes por sesión',
      'Herramientas esenciales',
    ],
    alcanza: 'Un taller corto al mes: una lluvia de ideas con votación, o una retro de equipo.',
    cta: 'Crear cuenta gratis',
    destacado: false,
  },
  {
    nombre: 'Diálogo',
    codigo: 'dialogo',
    precio: '$12',
    detalle: 'por mes',
    rasgos: [
      '8 horas de sala al mes (4 h por sesión)',
      'Hasta 10 participantes por sesión',
      'Catálogo completo · 73 herramientas',
      'Validación asíncrona de documentos',
    ],
    alcanza: 'Cuatro talleres, o un diagnóstico exprés completo de dos jornadas.',
    cta: 'Empezar con Diálogo',
    destacado: false,
  },
  {
    nombre: 'Tribuna',
    codigo: 'tribuna',
    precio: '$29',
    detalle: 'por mes',
    rasgos: [
      '16 horas de sala al mes, sin tope por sesión',
      'Hasta 20 participantes por sesión',
      'Micrófono de sala que separa a cada hablante',
      'Catálogo completo y validación de documentos',
    ],
    alcanza: 'Una consulta de tres jornadas al mes, o dos diagnósticos, u ocho talleres sueltos.',
    cta: 'Empezar con Tribuna',
    destacado: true,
  },
];

const ORGANIZACIONALES = [
  {
    nombre: 'Foro 1',
    codigo: 'foro1',
    precio: '$79',
    rasgos: ['1 facilitador', '40 horas de sala al mes', 'Hasta 30 participantes por sesión'],
    alcanza: 'Dos levantamientos completos al mes, o cuatro consultas de tres jornadas.',
  },
  {
    nombre: 'Foro 3',
    codigo: 'foro3',
    precio: '$249',
    rasgos: ['3 facilitadores en paralelo', '120 horas de sala al mes', 'Hasta 60 participantes por sesión'],
    alcanza: 'Tres equipos trabajando a la vez, cada uno con un levantamiento propio al mes.',
  },
  {
    nombre: 'Foro 5',
    codigo: 'foro5',
    precio: '$449',
    rasgos: ['5 facilitadores en paralelo', '300 horas de sala al mes', 'Hasta 120 participantes por sesión'],
    alcanza: 'Una unidad entera trabajando sin contar horas: 15 levantamientos al mes.',
  },
];

/** El hero rota fotos REALES de participación y una frase por foto. */
const ESCENAS = [
  { img: '/landing/hero-1.webp', frase: 'Cada voz queda en el acta' },
  { img: '/landing/hero-2.webp', frase: 'Del campo a la propuesta, sin perder una palabra' },
  { img: '/landing/hero-3.webp', frase: `${TOTAL_HERRAMIENTAS} herramientas de facilitación, listas para correr` },
  { img: '/landing/hero-4.webp', frase: 'Voz, texto, fotos y documentos: toda la evidencia, en un solo lugar' },
  { img: '/landing/hero-5.webp', frase: 'Cada afirmación cita la evidencia que la respalda' },
];

const LOGOS_CONFIANZA = [
  { nombre: 'Organismo Judicial de Guatemala', src: '/brand/tenants/oj-guatemala/oj.svg' },
  { nombre: 'Programa de las Naciones Unidas para el Desarrollo', src: '/brand/tenants/oj-guatemala/pnud.svg' },
  { nombre: 'PULSO · Inteligencia pública y territorial', src: '/landing/pulso.svg' },
];

const STATS = [
  { n: '+14 mil', l: 'aportes capturados' },
  { n: String(TOTAL_HERRAMIENTAS), l: 'herramientas de facilitación' },
  { n: '3', l: 'superficies sincronizadas' },
  { n: '100%', l: 'de lo afirmado, citable' },
];

/* Estilos compartidos de la landing: sombras teñidas de navy (una sombra gris
   sobre azul se ve sucia) y un lavado de degradado apenas perceptible que le
   da cuerpo a las tarjetas sin volverlas protagonistas. */
const ESTILOS_SUAVES = `
.tarjeta-suave{
  background:
    radial-gradient(140% 120% at 0% 0%, color-mix(in srgb, var(--color-primary-600) 5%, transparent), transparent 60%),
    var(--surface-raised);
  box-shadow: 0 1px 2px color-mix(in srgb, var(--color-navy-900) 6%, transparent),
              0 12px 28px -20px color-mix(in srgb, var(--color-navy-900) 45%, transparent);
  transition: transform .35s cubic-bezier(.22,.7,.3,1), box-shadow .35s cubic-bezier(.22,.7,.3,1);
}
.tarjeta-suave:hover{
  transform: translateY(-4px);
  box-shadow: 0 2px 4px color-mix(in srgb, var(--color-navy-900) 8%, transparent),
              0 22px 44px -24px color-mix(in srgb, var(--color-primary-600) 42%, transparent);
}
.seccion-lavada{
  background:
    radial-gradient(90% 60% at 85% 0%, color-mix(in srgb, var(--color-cyan-500) 7%, transparent), transparent 65%),
    radial-gradient(70% 55% at 5% 100%, color-mix(in srgb, var(--color-violet-500) 6%, transparent), transparent 60%),
    var(--surface-base);
}
.seccion-lavada-alt{
  background:
    radial-gradient(80% 55% at 10% 0%, color-mix(in srgb, var(--color-primary-600) 8%, transparent), transparent 62%),
    var(--surface-sunken);
}
@media (prefers-reduced-motion: reduce){ .tarjeta-suave{transition:none} .tarjeta-suave:hover{transform:none} }
`;

/** Retícula sutil del hero (blanco a 5%, patrón 48px). */
const reticula = {
  backgroundImage:
    'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)',
  backgroundSize: '48px 48px',
};

export function LandingPage() {
  useSurfaceTheme('bolsillo', 'light');
  const status = useAuthStore((s) => s.status);
  const hydrate = useAuthStore((s) => s.hydrate);
  useEffect(() => {
    if (status === 'idle') void hydrate();
  }, [status, hydrate]);
  const conSesion = status === 'authenticated';

  // Carrusel del hero: foto + frase avanzan juntas cada 6 s.
  const [escena, setEscena] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setEscena((e) => (e + 1) % ESCENAS.length), 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-surface-base">
      <style>{ESTILOS_SUAVES}</style>
      {/* ════════ HERO ════════ */}
      <header className="relative flex min-h-[92vh] flex-col overflow-hidden bg-navy">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-10 h-1 bg-brand-gradient"
          aria-hidden="true"
        />
        {/* capas: fotos reales de participación en crossfade + velo navy que
            garantiza contraste + retícula sutil. El gradiente de marca queda
            en la línea superior y la palabra clave. */}
        <div className="absolute inset-0" aria-hidden="true">
          {ESCENAS.map((e, i) => (
            <img
              key={e.img}
              src={e.img}
              alt=""
              loading={i === 0 ? 'eager' : 'lazy'}
              // blur + scale (el scale esconde los bordes que el blur revela)
              className="absolute inset-0 h-full w-full scale-105 object-cover blur-[4px] transition-opacity duration-[1800ms] ease-in-out"
              style={{ opacity: i === escena ? 1 : 0 }}
            />
          ))}
          {/* capa de color UNIFORME + degradado de fundido. OJO: inline con
              color-mix sobre el token — Tailwind DESCARTA en silencio los
              modificadores /NN sobre colores var() sin canal alfa (bug real:
              el velo bg-navy/80 nunca llegó al CSS y el texto quedó sobre la
              foto cruda). */}
          <div
            className="absolute inset-0"
            style={{ backgroundColor: 'color-mix(in srgb, var(--color-navy-900) 82%, transparent)' }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to bottom, transparent 55%, var(--color-navy-900) 100%)',
            }}
          />
          <div className="absolute inset-0 opacity-40" style={reticula} />
        </div>

        {/* barra */}
        <nav className="relative z-10 mx-auto flex w-full max-w-6xl items-center gap-5 px-5 py-5">
          <img src="/brand/logonegativo.png" alt="ÁGORA" className="h-8 w-auto" />
          <span className="flex-1" />
          <a
            href="#que-es"
            className="hidden font-sans text-sm text-white/70 transition hover:text-white sm:block"
          >
            Qué es
          </a>
          <a
            href="#metodos"
            className="hidden font-sans text-sm text-white/70 transition hover:text-white sm:block"
          >
            Método
          </a>
          <a
            href="#catalogo"
            className="hidden font-sans text-sm text-white/70 transition hover:text-white sm:block"
          >
            Herramientas
          </a>
          <a
            href="#planes"
            className="hidden font-sans text-sm text-white/70 transition hover:text-white sm:block"
          >
            Planes
          </a>
          <Link
            to="/entrar"
            className="inline-flex min-h-touch items-center gap-1.5 rounded-xl border border-white/20 bg-white/10 px-4 py-2 font-sans text-sm font-semibold text-white backdrop-blur transition hover:bg-white/20"
          >
            <LogIn className="h-4 w-4" aria-hidden="true" />
            {conSesion ? 'Ir a mi cuenta' : 'Entrar'}
          </Link>
        </nav>

        {/* claim */}
        <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-1 flex-col items-center justify-center px-5 pb-16 pt-8 text-center">
          <p className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/10 px-4 py-2 backdrop-blur">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
            </span>
            <span className="font-sans text-xs text-white/90 sm:text-sm">
              En producción con instituciones públicas de Guatemala
            </span>
          </p>

          <h1
            key={escena}
            className="max-w-3xl animate-[frase-entra_700ms_ease-out] font-display text-4xl font-bold leading-[1.1] text-white [text-shadow:0_2px_18px_rgba(11,19,43,.9)] sm:text-6xl"
          >
            {ESCENAS[escena].frase}
          </h1>
          <style>{`@keyframes frase-entra{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}`}</style>
          <p className="mt-6 max-w-2xl font-sans text-base leading-relaxed text-ice-200 [text-shadow:0_1px_12px_rgba(11,19,43,.9)] sm:text-lg">
            No es una grabadora de reuniones. Es el método de diseño centrado en
            las personas de punta a punta: {TOTAL_HERRAMIENTAS} herramientas que
            producen resultados verificables, un análisis por jornada que hereda
            el del día anterior, y un documento final donde{' '}
            <strong className="text-white">cada afirmación dice de quién
            salió</strong>.
          </p>

          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link
              to={conSesion ? '/entrar' : '/login'}
              className="inline-flex min-h-touch items-center gap-2 rounded-xl bg-white px-7 py-3.5 font-sans text-sm font-bold text-navy shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl"
            >
              {conSesion ? 'Ir a mi sala' : 'Crear mi cuenta gratis'}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <a
              href="#planes"
              className="inline-flex min-h-touch items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-7 py-3.5 font-sans text-sm font-semibold text-white backdrop-blur transition hover:bg-white/20"
            >
              Ver planes
            </a>
          </div>

          {/* stats */}
          <dl className="mt-14 grid w-full max-w-3xl grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-4">
            {STATS.map((s) => (
              <div key={s.l}>
                <dt className="sr-only">{s.l}</dt>
                <dd className="font-display text-3xl font-bold text-white">{s.n}</dd>
                <dd className="mt-1 font-sans text-xs text-ice-200">{s.l}</dd>
              </div>
            ))}
          </dl>
          <a
            href="#que-es"
            aria-label="Bajar a conocer la plataforma"
            className="mt-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white/70 transition hover:bg-white/10 hover:text-white"
          >
            <ArrowRight className="h-4 w-4 rotate-90" aria-hidden="true" />
          </a>
        </div>
      </header>

      {/* ════════ CONFIANZA ════════ */}
      <section
        aria-label="Instituciones que han confiado"
        className="border-b border-border bg-surface-raised py-10"
      >
        <p className="mb-7 text-center font-sans text-xs font-semibold uppercase tracking-widest text-content-muted">
          Han confiado en nosotros
        </p>
        {/* Cajas de igual tamaño con object-contain: los tres logos tienen
            proporciones muy distintas (el del PNUD es vertical, el de PULSO
            apaisado) y forzarlos a la misma altura los descuadraba. */}
        <ul className="mx-auto flex max-w-4xl flex-wrap items-start justify-center gap-x-12 gap-y-8 px-5">
          {LOGOS_CONFIANZA.map((l) => (
            <li key={l.nombre} className="flex w-40 flex-col items-center gap-3 text-center">
              <span className="flex h-16 w-full items-center justify-center">
                <img
                  src={l.src}
                  alt=""
                  className="max-h-16 max-w-full object-contain"
                  loading="lazy"
                />
              </span>
              <span className="font-sans text-xs font-medium leading-snug text-content-muted">
                {l.nombre}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* ════════ QUÉ ES ════════ */}
      <section id="que-es" className="seccion-lavada scroll-mt-16">
        <div className="mx-auto max-w-6xl px-5 py-20">
        <p className="font-sans text-sm font-semibold uppercase tracking-widest text-primary">
          Qué es ÁGORA
        </p>
        <h2 className="mt-2 max-w-3xl font-display text-3xl font-bold leading-tight text-content">
          El método de diseño centrado en las personas, con evidencia que se
          puede citar
        </h2>
        <div className="medida mt-4 max-w-2xl">
          <p className="font-sans text-base leading-relaxed text-content-muted">
            Un taller bien facilitado produce acuerdos valiosos y los pierde casi
            todos: quedan en papelógrafos fotografiados, en una relatoría que
            alguien escribe de memoria dos semanas después, y en la palabra de
            quien estuvo. <strong className="text-content">ÁGORA corre el mismo
            método —Design Thinking, Design Sprint, diseño centrado en las
            personas— pero cada paso deja un producto verificable</strong>, y al
            final un documento donde cada afirmación dice de quién salió.
          </p>
        </div>

        {/* La cadena: de lo que alguien dice al documento entregable. */}
        <ol className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              paso: 'Entra la evidencia',
              detalle:
                'Cada quien aporta como puede: hablando, escribiendo, votando, fotografiando el papelógrafo o subiendo el documento que tenía guardado. Todo queda con su autor y su hora, y lo hablado, transcrito.',
            },
            {
              paso: 'La herramienta produce',
              detalle:
                'Cada dinámica entrega lo suyo con esa evidencia: un ranking votado, un mapa de actores, una matriz de validación. Calculado, no interpretado.',
            },
            {
              paso: 'El día se sintetiza',
              detalle:
                'Al cerrar la jornada queda su análisis —hallazgos, decisiones, tensiones, vacíos— y la memoria que hereda el día siguiente.',
            },
            {
              paso: 'Sale el documento',
              detalle:
                'La IA redacta sobre esa evidencia y cita cada afirmación. Lo que no tiene respaldo, lo declara en vez de rellenarlo.',
            },
          ].map((n, idx) => (
            <li key={n.paso} className="relative">
              <Revelar delay={idx * 110}>
                <div className="tarjeta-suave relative h-full rounded-2xl border border-border p-5 pt-7">
              <span className="absolute -top-3 left-5 flex h-7 items-center rounded-full bg-brand-gradient px-3 font-sans text-[11px] font-bold uppercase tracking-wide text-white shadow">
                {idx + 1}
              </span>
              <h3 className="font-display text-base font-semibold text-content">{n.paso}</h3>
              <p className="mt-1.5 font-sans text-sm leading-relaxed text-content-muted">
                {n.detalle}
              </p>
                </div>
              </Revelar>
              {idx < 3 && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -right-3 top-1/2 hidden h-6 w-6 -translate-y-1/2 items-center justify-center text-border-strong lg:flex"
                >
                  <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </li>
          ))}
        </ol>
        <style>{`@keyframes entra{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}
          @media (prefers-reduced-motion: reduce){ol li{animation:none!important}}`}</style>

        <div className="mt-10">
          <DiagramaTrazabilidad />
        </div>

        <p className="medida mt-8 max-w-2xl font-sans text-base leading-relaxed text-content-muted">
          Esa cadena es la diferencia. Un transcriptor te devuelve lo que se
          dijo; ÁGORA te devuelve <strong className="text-content">lo que se
          decidió, con quién lo dijo al lado</strong> — que es lo que se necesita
          cuando alguien pregunta «¿de dónde salió esta conclusión?».
          </p>
        </div>
      </section>

      {/* ════════ PRODUCTO ════════ */}
      <section id="producto" className="mx-auto max-w-6xl scroll-mt-16 px-5 py-20">
        <p className="font-sans text-sm font-semibold uppercase tracking-widest text-primary">
          La plataforma
        </p>
        <h2 className="mt-2 max-w-xl font-display text-3xl font-bold leading-tight text-content">
          Tres superficies, una sola conversación
        </h2>
        <p className="mt-3 max-w-2xl font-sans text-base text-content-muted">
          El participante aporta desde su teléfono, quien facilita conduce desde la
          Cabina y la sala entera ve la Pantalla proyectada — todo sincronizado en
          tiempo real, con la evidencia entrando al acta en el momento.
        </p>

        <div className="mt-8">
          <DiagramaSuperficies />
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              icon: IconoAporte,
              titulo: 'El Bolsillo',
              detalle:
                'Cada participante aporta desde su teléfono como le sirva: hablando, escribiendo, votando, fotografiando o subiendo un archivo. Si se cae la señal, el aporte espera y se envía solo.',
            },
            {
              icon: IconoVoz,
              titulo: 'La voz también cuenta',
              detalle:
                'Mantené presionado y hablá: la transcripción cae en su casilla. El micrófono de sala separa a cada hablante de la mesa.',
            },
            {
              icon: IconoCita,
              titulo: 'Todo citable',
              detalle:
                'Cada salida cita el aporte que la respalda, con autor y hora. Lo que no tiene evidencia se marca — no se inventa.',
            },
            {
              icon: IconoValidar,
              titulo: 'Validación asíncrona',
              detalle:
                'Un link por documento: cada comentario llega con nombre y transcripción, sin coordinar agendas ni salas.',
            },
          ].map(({ icon: Icon, titulo, detalle }, idx) => (
            <Revelar key={titulo} delay={idx * 90}>
              <article className="tarjeta-suave group h-full rounded-2xl border border-border p-5">
                {/* El icono va sobre un halo del propio degradado, no sobre un
                    bloque sólido: se integra con el fondo en vez de pegarse. */}
                <span className="relative inline-flex h-14 w-14 items-center justify-center">
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 rounded-2xl opacity-70 transition duration-300 group-hover:opacity-100"
                    style={{
                      background:
                        'radial-gradient(120% 120% at 30% 20%, color-mix(in srgb, var(--color-primary-600) 16%, transparent), transparent 70%)',
                    }}
                  />
                  <Icon className="relative h-8 w-8" />
                </span>
                <h3 className="mt-4 font-display text-base font-semibold text-content">{titulo}</h3>
                <p className="mt-1.5 font-sans text-sm leading-relaxed text-content-muted">
                  {detalle}
                </p>
              </article>
            </Revelar>
          ))}
        </div>
      </section>

      {/* ════════ CÓMO FUNCIONA ════════ */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <p className="font-sans text-sm font-semibold uppercase tracking-widest text-primary">
            Cómo funciona
          </p>
          <h2 className="mt-2 max-w-xl font-display text-3xl font-bold leading-tight text-content">
            Del método al acta en tres movimientos
          </h2>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {[
              {
                n: '1',
                titulo: 'Componé el método',
                detalle:
                  'Armá tu proceso en el compositor: elegí herramientas del catálogo, encadená sus salidas y entradas, o partí de una plantilla lista — de una dinámica suelta a un Design Sprint de ocho medios días.',
              },
              {
                n: '2',
                titulo: 'Correlo en vivo',
                detalle:
                  'Cada participante aporta desde su teléfono, vos conducís desde la Cabina y la sala ve la Pantalla. Texto, votos, fotos y voz — transcrita al instante, con el micrófono de sala separando a cada hablante.',
              },
              {
                n: '3',
                titulo: 'Entregá la propuesta',
                detalle:
                  'Todo lo aportado queda citable: la propuesta final referencia cada afirmación a su aporte, con autor y hora. Lo que no tiene respaldo se marca — el acta no se discute, se consulta.',
              },
            ].map((paso) => (
              <article key={paso.n} className="relative rounded-2xl border border-border bg-surface-raised p-6 pt-8 shadow-sm">
                <span className="absolute -top-5 left-6 flex h-10 w-10 items-center justify-center rounded-xl bg-brand-gradient font-display text-lg font-bold text-white shadow-md">
                  {paso.n}
                </span>
                <h3 className="font-display text-lg font-semibold text-content">{paso.titulo}</h3>
                <p className="mt-2 font-sans text-sm leading-relaxed text-content-muted">{paso.detalle}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ DE DÓNDE VIENE ════════ */}
      <section id="metodos" className="seccion-lavada-alt scroll-mt-16 border-t border-border">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <p className="font-sans text-sm font-semibold uppercase tracking-widest text-primary">
            De dónde viene
          </p>
          <h2 className="mt-2 max-w-2xl font-display text-3xl font-bold leading-tight text-content">
            Nada de esto lo inventamos: son métodos probados, puestos a producir
          </h2>
          <p className="mt-3 max-w-2xl font-sans text-base text-content-muted">
            Las {TOTAL_HERRAMIENTAS} herramientas del catálogo salen de prácticas
            reconocidas de diseño y participación. Lo que ÁGORA agrega no es una
            metodología nueva: es que cada dinámica deje un resultado citable en
            vez de un papelógrafo.
          </p>

          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {[
              {
                metodo: 'Design Thinking',
                origen: 'd.school de Stanford · IDEO',
                aporta:
                  'Las cinco familias del catálogo SON sus cinco etapas: empatizar, definir, idear, prototipar y testear. Es la columna vertebral de cómo se compone un proceso.',
                ejemplos: ['Lo que vive la gente', 'Hechos, supuestos y preguntas', '¿Cómo podríamos…?'],
              },
              {
                metodo: 'Design Sprint',
                origen: 'Jake Knapp · Google Ventures',
                aporta:
                  'El repertorio de decisión rápida: bocetar en silencio, votar sin discutir, y que quien decide decida. La plantilla del sprint completo está lista para instanciar.',
                ejemplos: ['Ocho ideas contra reloj', 'Museo y votación de bocetos', 'Voto final con supervoto', 'Robar con orgullo'],
              },
              {
                metodo: 'Diseño centrado en las personas',
                origen: 'HCD · ISO 9241-210',
                aporta:
                  'La disciplina de ir a ver antes de proponer: investigación en territorio, la voz textual de quien vive el problema y la prueba con gente real antes de construir.',
                ejemplos: ['Entrevista en territorio', 'Tu diario de la semana', 'El recorrido de la persona', 'Prueba con usuario'],
              },
              {
                metodo: 'Participación pública',
                origen: 'Práctica municipal y de cooperación',
                aporta:
                  'Lo que un proceso público necesita y un taller de producto no: convocatoria, deliberación abierta, votación con reglas y acta firmada que después se puede auditar.',
                ejemplos: ['Mapa de actores', 'Subasta de votos', 'Acta firmada', 'Cabildo abierto'],
              },
            ].map((m, idx) => (
              <Revelar key={m.metodo} delay={idx * 90}>
              <article className="tarjeta-suave h-full rounded-2xl border border-border p-6">
                <h3 className="font-display text-lg font-bold text-content">{m.metodo}</h3>
                <p className="mt-0.5 font-sans text-xs uppercase tracking-wide text-content-muted">
                  {m.origen}
                </p>
                <p className="mt-3 font-sans text-sm leading-relaxed text-content-muted">
                  {m.aporta}
                </p>
                <ul className="mt-4 flex flex-wrap gap-1.5">
                  {m.ejemplos.map((e) => (
                    <li
                      key={e}
                      className="rounded-full bg-surface-sunken px-2.5 py-1 font-sans text-[11px] font-medium text-content"
                    >
                      {e}
                    </li>
                  ))}
                </ul>
              </article>
              </Revelar>
            ))}
          </div>

          <p className="mt-6 max-w-2xl font-sans text-sm leading-relaxed text-content-muted">
            Los nombres de las herramientas están en el idioma de quien
            participa, no en el del manual: «Ocho ideas contra reloj» en vez de
            <em> Crazy 8s</em>, «Robar con orgullo» en vez de <em>lightning
            demos</em>. Quien facilita reconoce el método; quien participa
            entiende qué tiene que hacer.
          </p>
        </div>
      </section>

      {/* ════════ CATÁLOGO ════════ */}
      <SeccionCatalogo />

      {/* ════════ PLANES ════════ */}
      <section id="planes" className="seccion-lavada-alt scroll-mt-16 border-t border-border">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <p className="font-sans text-sm font-semibold uppercase tracking-widest text-primary">
            Planes
          </p>
          <h2 className="mt-2 font-display text-3xl font-bold text-content">
            Empezá gratis, crecé cuando la sala crezca
          </h2>
          <p className="mt-2 font-sans text-sm text-content-muted">
            Precios de lanzamiento en dólares (USD). Cancelás cuando quieras.
          </p>

          <h3 className="mt-10 font-display text-base font-semibold text-content">
            Para personas
            <span className="ml-2 font-sans text-xs font-normal text-content-muted">
              trabajás dentro de ÁGORA, con su marca — ideal para consultores y facilitadores
            </span>
          </h3>
          <div className="mt-4 grid gap-5 md:grid-cols-3">
            {PERSONALES.map((p) =>
              p.destacado ? (
                <div key={p.nombre} className="rounded-3xl bg-brand-gradient p-[2px] shadow-lg">
                  <article className="relative flex h-full flex-col rounded-[calc(1.5rem-2px)] bg-surface-raised p-6">
                    <span className="absolute -top-3.5 left-6 rounded-full bg-brand-gradient px-3 py-1 font-sans text-[11px] font-bold uppercase tracking-wide text-white shadow">
                      Más elegido
                    </span>
                    <TarjetaPlanCuerpo p={p} />
                    <Link
                      to={p.codigo ? `/comprar/${p.codigo}` : '/login'}
                      className="mt-6 flex min-h-touch items-center justify-center rounded-xl bg-primary px-4 py-3 font-sans text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
                    >
                      {p.cta}
                    </Link>
                  </article>
                </div>
              ) : (
                <article
                  key={p.nombre}
                  className="tarjeta-suave flex flex-col rounded-3xl border border-border p-6"
                >
                  <TarjetaPlanCuerpo p={p} />
                  <Link
                    to={p.codigo ? `/comprar/${p.codigo}` : '/login'}
                    className="mt-6 flex min-h-touch items-center justify-center rounded-xl border border-border-strong px-4 py-3 font-sans text-sm font-semibold text-content transition hover:bg-surface-sunken"
                  >
                    {p.cta}
                  </Link>
                </article>
              ),
            )}
          </div>

          <h3 className="mt-12 font-display text-base font-semibold text-content">
            Para organizaciones
            <span className="ml-2 font-sans text-xs font-normal text-content-muted">
              instituciones, gobierno, municipalidades, ONG y empresas — con tu propia identidad institucional
            </span>
          </h3>
          <div className="mt-3">
            <DiagramaTenant />
          </div>
          <div className="mt-3 rounded-2xl border border-border bg-surface-raised p-5">
            <p className="font-sans text-sm leading-relaxed text-content-muted">
              <span className="font-semibold text-content">Tu institución, no la nuestra.</span>{' '}
              Los planes de organización crean un espacio propio con tu identidad:
              tu gente entra por una dirección con el logo y el nombre de tu
              institución, las constancias de participación salen con tu escudo y
              el de tu cooperante, y tus procesos y tu evidencia viven separados
              de los de cualquier otra organización. Vos administrás a tu equipo,
              tus facilitadores y tu marca, sin pasar por nosotros. En los planes
              personales el trabajo ocurre dentro de ÁGORA y lleva nuestra marca.
            </p>
          </div>
          <div className="mt-4 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {ORGANIZACIONALES.map((p) => (
              <article
                key={p.nombre}
                className="tarjeta-suave flex flex-col rounded-3xl border border-border p-6"
              >
                <h4 className="font-display text-lg font-bold text-content">{p.nombre}</h4>
                <p className="mt-1">
                  <span className="font-display text-3xl font-bold text-content">{p.precio}</span>
                  <span className="ml-1.5 font-sans text-sm text-content-muted">por mes</span>
                </p>
                <ul className="mt-4 space-y-2.5">
                  {p.rasgos.map((r) => (
                    <li key={r} className="flex items-start gap-2 font-sans text-sm text-content">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
                      {r}
                    </li>
                  ))}
                </ul>
                <p className="mt-4 flex-1 border-t border-border pt-3 font-sans text-xs leading-relaxed text-content-muted">
                  <span className="font-semibold text-content">Te alcanza para: </span>
                  {p.alcanza}
                </p>
                <Link
                  to={`/comprar/${p.codigo}`}
                  className="mt-6 flex min-h-touch items-center justify-center rounded-xl border border-border-strong px-4 py-3 font-sans text-sm font-semibold text-content transition hover:bg-surface-sunken"
                >
                  Contratar {p.nombre}
                </Link>
              </article>
            ))}

            {/* Ágora Plena: la tarjeta institucional, con el hero en miniatura */}
            <article className="relative flex flex-col overflow-hidden rounded-3xl bg-navy p-6 shadow-lg">
              <div
                className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/30 blur-3xl"
                aria-hidden="true"
              />
              <div
                className="pointer-events-none absolute -bottom-12 -left-8 h-36 w-36 rounded-full bg-violet/20 blur-3xl"
                aria-hidden="true"
              />
              <h4 className="relative font-display text-lg font-bold text-white">Ágora Plena</h4>
              <p className="relative mt-1 font-display text-3xl font-bold text-white">A tu medida</p>
              <ul className="relative mt-4 flex-1 space-y-2.5">
                {[
                  'Facilitadores ilimitados',
                  'Sin topes de procesos, horas ni participantes',
                  'Observatorio y memoria institucional',
                  'Acompañamiento del equipo ÁGORA',
                ].map((r) => (
                  <li key={r} className="flex items-start gap-2 font-sans text-sm text-white/90">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan" aria-hidden="true" />
                    {r}
                  </li>
                ))}
              </ul>
              <a
                href="mailto:info@inteligencia.com.gt?subject=ÁGORA%20Plena"
                className="relative mt-6 flex min-h-touch items-center justify-center rounded-xl bg-white px-4 py-3 font-sans text-sm font-bold text-navy shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
              >
                Contactar
              </a>
            </article>
          </div>
        </div>
      </section>

      {/* ════════ CTA FINAL ════════ */}
      <section className="relative overflow-hidden bg-navy">
        <div className="absolute inset-0 opacity-40" style={reticula} aria-hidden="true" />
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 h-72 w-[36rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/20 blur-3xl"
          aria-hidden="true"
        />
        <div className="relative mx-auto flex max-w-4xl flex-col items-center px-5 py-16 text-center">
          <h2 className="max-w-xl font-display text-2xl font-bold text-white sm:text-3xl">
            Tu próxima sesión puede quedar entera en el acta
          </h2>
          <p className="mt-3 max-w-lg font-sans text-sm text-ice-200">
            Creá tu cuenta gratis, armá tu primer proceso con una plantilla y corré
            una sesión real hoy mismo.
          </p>
          <Link
            to="/login"
            className="mt-7 inline-flex min-h-touch items-center gap-2 rounded-xl bg-white px-7 py-3.5 font-sans text-sm font-bold text-navy shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl"
          >
            Empezar ahora
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </section>

      {/* ════════ PIE ════════ */}
      <footer className="border-t border-border px-5 py-8">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-2 font-sans text-xs text-content-muted">
          <span className="font-display font-bold text-content">ÁGORA</span>
          <span>Grupo InteligencIA · Guatemala</span>
          <a href="mailto:info@inteligencia.com.gt" className="hover:text-content hover:underline">
            info@inteligencia.com.gt
          </a>
          <span className="flex-1" />
          <Link to="/privacidad" className="hover:text-content hover:underline">
            Privacidad
          </Link>
          <Link to="/terminos" className="hover:text-content hover:underline">
            Términos
          </Link>
        </div>
      </footer>
    </div>
  );
}

function TarjetaPlanCuerpo({ p }: { p: (typeof PERSONALES)[number] }) {
  return (
    <>
      <h4 className="font-display text-lg font-bold text-content">{p.nombre}</h4>
      <p className="mt-1">
        <span className="font-display text-3xl font-bold text-content">{p.precio}</span>
        <span className="ml-1.5 font-sans text-sm text-content-muted">{p.detalle}</span>
      </p>
      <ul className="mt-4 space-y-2.5">
        {p.rasgos.map((r) => (
          <li key={r} className="flex items-start gap-2 font-sans text-sm text-content">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
            {r}
          </li>
        ))}
      </ul>
      <p className="mt-4 flex-1 border-t border-border pt-3 font-sans text-xs leading-relaxed text-content-muted">
        <span className="font-semibold text-content">Te alcanza para: </span>
        {p.alcanza}
      </p>
    </>
  );
}


function SeccionCatalogo() {
  const [familia, setFamilia] = useState(0);
  const f = CATALOGO[familia];
  return (
    <section id="catalogo" className="border-t border-border bg-surface-sunken">
      <div className="mx-auto max-w-6xl px-5 py-20">
        <p className="font-sans text-sm font-semibold uppercase tracking-widest text-primary">
          El catálogo
        </p>
        <h2 className="mt-2 max-w-2xl font-display text-3xl font-bold leading-tight text-content">
          {TOTAL_HERRAMIENTAS} herramientas, y lo que cada una te deja
        </h2>
        <p className="mt-3 max-w-2xl font-sans text-base text-content-muted">
          Una herramienta no es el producto: el producto es lo que produce. Cada
          una sabe qué capturar y qué entregar —un ranking votado, un mapa de
          actores, una matriz de validación— y ese resultado, ya citado, es lo
          que alimenta el análisis del día y el documento final.
        </p>

        <div className="mt-6 flex gap-1.5 overflow-x-auto pb-1">
          {CATALOGO.map((fam, i) => {
            const Icono = ICONOS_FAMILIA[fam.nombre];
            const color = COLOR_FAMILIA[fam.nombre];
            const activa = i === familia;
            return (
              <button
                key={fam.nombre}
                type="button"
                onClick={() => setFamilia(i)}
                className="group flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 font-sans text-sm transition duration-300"
                style={{
                  // Cada etapa del método tiene su tono: se reconoce por color
                  // antes que por leer la palabra.
                  borderColor: activa ? color : 'var(--border-default)',
                  background: activa
                    ? `color-mix(in srgb, ${color} 12%, var(--surface-raised))`
                    : 'var(--surface-raised)',
                  color: activa ? 'var(--text-primary)' : 'var(--text-muted)',
                  fontWeight: activa ? 600 : 500,
                  boxShadow: activa
                    ? `0 10px 24px -18px color-mix(in srgb, ${color} 80%, transparent)`
                    : 'none',
                }}
              >
                {Icono && (
                  <span className="h-4 w-4">
                    <Icono color={activa ? color : 'currentColor'} />
                  </span>
                )}
                {fam.nombre}
                <span style={{ color: activa ? color : undefined }} className="opacity-70">
                  {fam.herramientas.length}
                </span>
              </button>
            );
          })}
        </div>
        <div
          className="mt-4 rounded-2xl border p-5"
          style={{
            borderColor: `color-mix(in srgb, ${COLOR_FAMILIA[f.nombre]} 35%, var(--border-default))`,
            background: `radial-gradient(120% 140% at 0% 0%, color-mix(in srgb, ${COLOR_FAMILIA[f.nombre]} 9%, transparent), transparent 62%), var(--surface-raised)`,
          }}
        >
          <p
            className="font-sans text-xs font-semibold uppercase tracking-widest"
            style={{ color: COLOR_FAMILIA[f.nombre] }}
          >
            Lo que te llevás de {f.nombre.toLowerCase()}
          </p>
          <p className="mt-1.5 max-w-2xl font-sans text-base leading-relaxed text-content">
            {f.producto}
          </p>
          <p className="mt-2 max-w-2xl font-sans text-sm text-content-muted">{f.descripcion}</p>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {f.herramientas.map((h, idx) => (
            <article
              key={h.n}
              className="tarjeta-suave rounded-xl border border-border p-4"
              style={{ animation: `entra 480ms cubic-bezier(.22,.7,.3,1) ${Math.min(idx, 12) * 35}ms both` }}
            >
              <h3 className="font-display text-sm font-semibold text-content">{h.n}</h3>
              {h.q && (
                <p className="mt-1 font-sans text-xs leading-relaxed text-content-muted">{h.q}</p>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
